import { useEffect, useRef } from 'react';
import { useFeedbackMarkup } from './FeedbackMarkupProvider.jsx';

export function FeedbackEndSessionDialog() {
  const { state, closeEndSession, endFeedbackSession, retrySessionReconciliation } =
    useFeedbackMarkup();
  const dialogRef = useRef(null);
  const cancelRef = useRef(null);

  useEffect(() => {
    if (!state.endSessionOpen) return;
    cancelRef.current?.focus?.();
  }, [state.endSessionOpen]);

  if (!state.endSessionOpen || !state.package) return null;

  const pkg = state.package;
  const busy = state.endSessionStatus === 'saving' || state.endSessionStatus === 'discarding';
  const verificationBlocked =
    state.recoveryStatus === 'checking' || state.recoveryStatus === 'error';
  // A freshly captured stage is not mirrored into package.screens until its
  // first mark/save — count it here exactly like the banner does, or "Exit
  // feedback mode" would silently discard a real capture as "empty".
  const stageScreenId = state.stage?.screenId || state.stage?.metadata?.screenId || null;
  const stageIsUnregistered =
    Boolean(state.stage) &&
    !(pkg.screens || []).some((screen) => (screen.screenId || screen.id) === stageScreenId);
  const screenCount =
    (pkg.screenCount || pkg.screens?.length || 0) + (stageIsUnregistered ? 1 : 0);
  const annotationCount =
    (pkg.annotationCount || 0) +
    (stageIsUnregistered ? (state.stage?.metadata?.annotations || []).length : 0);
  // An empty session (no screens, no marks) has nothing to save or discard —
  // exiting is a plain local switch-off with no server dependency.
  const isEmptySession = screenCount === 0 && annotationCount === 0;
  const saveFailed = state.endSessionStatus === 'failed';
  const degradedExitAvailable = Boolean(state.endSessionDegradedExitAvailable);

  function onKeyDown(event) {
    if (event.key === 'Escape' && !busy) {
      event.preventDefault();
      closeEndSession();
      return;
    }
    if (event.key === 'Tab') {
      const focusable = Array.from(
        dialogRef.current?.querySelectorAll('button:not(:disabled)') || [],
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  return (
    <div className="feedback-modal-backdrop" role="presentation">
      <section
        ref={dialogRef}
        tabIndex={-1}
        className="feedback-confirm-dialog feedback-end-session-dialog"
        role="dialog"
        aria-modal="true"
        aria-busy={busy}
        aria-labelledby="feedback-end-session-title"
        data-testid="feedback-end-session-dialog"
        onKeyDown={onKeyDown}
      >
        <div className="feedback-panel-kicker">Feedback mode</div>
        <h3 id="feedback-end-session-title">
          {isEmptySession ? 'Exit feedback mode?' : 'End feedback session?'}
        </h3>
        <p>
          {isEmptySession ? (
            <>Nothing has been captured yet, so exiting simply turns feedback mode off.</>
          ) : (
            <>
              <strong>{pkg.title || 'This feedback package'}</strong> will be saved as a draft you
              can reopen from My feedback. The feedback-mode banner closes after the save succeeds.
            </>
          )}
        </p>
        <div className="feedback-end-session-summary" aria-label="Feedback package contents">
          <span>
            <strong>{screenCount}</strong> screen{screenCount === 1 ? '' : 's'}
          </span>
          <span>
            <strong>{annotationCount}</strong> mark{annotationCount === 1 ? '' : 's'}
          </span>
        </div>
        {state.recoveryStatus === 'checking' ? (
          <p className="feedback-end-session-notice" aria-live="polite">
            PRIME is checking whether the saved draft is still available. Wait for verification to
            finish, or cancel to keep working.
          </p>
        ) : null}
        {state.recoveryStatus === 'error' ? (
          <div className="feedback-end-session-notice" role="alert">
            <span>{state.recoveryMessage}</span>
            <button
              type="button"
              className="btn ghost sm"
              onClick={() => void retrySessionReconciliation()}
              disabled={busy}
            >
              Retry verification
            </button>
          </div>
        ) : null}
        {state.endSessionError ? (
          <p className="feedback-error-text" role="alert" data-testid="feedback-end-session-error">
            {state.endSessionError}
          </p>
        ) : null}
        <div className="feedback-confirm-actions feedback-end-session-actions">
          <button
            type="button"
            ref={cancelRef}
            className="btn ghost"
            disabled={busy}
            onClick={closeEndSession}
          >
            Cancel
          </button>
          {isEmptySession ? (
            <button
              type="button"
              className="btn primary"
              data-testid="feedback-end-session-exit-empty"
              disabled={busy}
              onClick={() => void endFeedbackSession({ disposition: 'exit-empty' })}
            >
              Exit feedback mode
            </button>
          ) : (
            <>
              {state.recoveryStatus === 'error' || pkg.localOnly ? (
                // Discard survives ONLY for sessions that the typed-DELETE
                // path cannot handle: verification-failed sessions and
                // local-only packages (no server row to delete). While
                // verification is merely still checking, the package may be
                // perfectly saveable — no destructive option is offered.
                <button
                  type="button"
                  className="btn danger"
                  disabled={busy}
                  onClick={() => void endFeedbackSession({ disposition: 'discard' })}
                >
                  {state.endSessionStatus === 'discarding' ? 'Discarding…' : 'Discard package'}
                </button>
              ) : null}
              {saveFailed && !degradedExitAvailable ? (
                <button
                  type="button"
                  className="btn ghost"
                  data-testid="feedback-end-session-exit-keep-local"
                  disabled={busy}
                  onClick={() => void endFeedbackSession({ disposition: 'exit-keep-local' })}
                >
                  Exit anyway — keep my work on this device
                </button>
              ) : null}
              {degradedExitAvailable ? (
                <button
                  type="button"
                  className="btn danger"
                  data-testid="feedback-end-session-exit-keep-degraded"
                  disabled={busy}
                  onClick={() =>
                    void endFeedbackSession({ disposition: 'exit-keep-degraded' })
                  }
                >
                  Exit with marks and notes only
                </button>
              ) : null}
              <button
                type="button"
                className="btn primary"
                disabled={busy || verificationBlocked}
                onClick={() => void endFeedbackSession({ disposition: 'save' })}
              >
                {state.endSessionStatus === 'saving' ? 'Saving…' : 'Save draft and end'}
              </button>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
